import { scopedBrowserPath } from "./tenantRoute";
import { ApiSectionGroup, ApiFragmentNode, ApiDocument, ApiBounds } from "../shared/interfaces";
import { DOCUMENTS_LIBRARY_PATH } from "../shared/constants";
import { DOCUMENT_ALIASES } from "../shared/documentTypes";
import type { DocumentAlias } from "../shared/documentTypes";
// Type-only: erased at compile time, so this pulls none of api/run's runtime deps (axios).
// Type-only: erased at compile time, so this pulls none of the generated module's runtime values.
import type {
    NavigationSuggestionKind,
    NavigationSuggestionRoute,
} from "../api/navigation.generated";
import { sectionGroupForFilePage } from "./sectionGroups";
import type { OverlayLayerRef } from "../components/overlay/overlayLayerRefs";
import { withOverlayDeepLink } from "../components/overlay/zoomToTarget";
import type { WorkbenchSeedKind } from "../shared/workbenchContract";

export { DOCUMENT_ALIASES } from "../shared/documentTypes";
export type { DocumentAlias } from "../shared/documentTypes";

/** Routes where chat IS the page (home launcher + full chat) — the Marvin
 *  sidekick entry is redundant there and stays hidden. */
export function isChatNativeRoute(pathname: string): boolean {
    return pathname === "/" || pathname === "/c" || pathname.startsWith("/c/");
}

/**
 * Extracts the conversation id from a top-level `/c/:id` route, or null.
 * Returns null for `/c`, the shared-snapshot route `/c/shared/:shareId`, and any
 * non-conversation path — so feedback submitted while viewing a read-only share
 * (or anywhere off a conversation) never mints a new share.
 */
export function conversationIdFromPath(pathname: string): string | null {
    const match = pathname.match(/^\/c\/([^/]+)\/?$/);
    if (!match) return null;
    let id: string;
    try {
        id = decodeURIComponent(match[1]);
    } catch {
        // Malformed percent-encoding — treat as a non-conversation path.
        return null;
    }
    return id === "shared" ? null : id;
}

/**
 * Type guard to check if a string is a valid document alias.
 */
export function isDocumentAlias(value: string | undefined): value is DocumentAlias {
    return value !== undefined && value in DOCUMENT_ALIASES;
}

// Module-level cache for aliased documents
let cachedAliasedDocuments: ApiDocument[] | null = null;

export const setAliasedDocuments = (documents: ApiDocument[] | null) => {
    cachedAliasedDocuments = documents;
};

// Unified type for anything with an extractable ID
type IdSource =
    | string
    | { id: string }
    | { file_id: string }
    | { fragment_node_id: string }
    | ApiSectionGroup
    | ApiFragmentNode;

// Route path builders. Exported so the one spelling of a route lives here: a page
// that hardcodes its own path drifts the moment the route moves.
export const PATHS = {
    doc: (id: string) => `/doc/${id}`,
    sectionGroup: (docId: string, sgId: string) => `/doc/${docId}/sg/${sgId}`,
    node: (docId: string, nodeId: string) => `/doc/${docId}/node/${nodeId}`,
    sectionGroupNode: (docId: string, sgId: string, nodeId: string) =>
        `/doc/${docId}/sg/${sgId}/node/${nodeId}`,
    connection: (docId: string, sgId: string, connId: string) =>
        `/doc/${docId}/sg/${sgId}/c/${connId}`,
    constructabilityReview: "/w/cr/review",
    constructabilityDashboard: "/w/cr",
    documents: "/documents",
    rfi: (id: string) => `/w/rfi/${id}`,
    submittal: (id: string) => `/w/submittal/${id}`,
    submittalRegister: (docId: string) => `/w/submittal-register/${docId}`,
    // Bare app-page landings for Marvin `suggest_navigation` (no entity id).
    home: "/",
    search: "/w/search",
    controlTower: "/w/control-tower",
    // The RFI / Submittal DASHBOARDS (document-center tables), not the review workflows.
    rfis: "/documents/rfi",
    submittals: "/documents/submittal",
    // The RFI Assistant review workflow (distinct from the `rfis` dashboard above).
    rfiAssistant: "/w/rfi",
    submittalRegistry: "/w/submittal-registry",
    revisions: "/sheets/revisions",
    // A Marvin sheet (a table whose rows are conversations) and a row's shared conversation.
    marvinSheet: (sheetId: string) => `/c/sheets/${sheetId}`,
    sharedConversation: (shareId: string) => `/c/shared/${shareId}`,
    integrations: "/integrations",
    upload: DOCUMENTS_LIBRARY_PATH,
    /** Marvin Explore (renamed from Workbench); `/w/workbench` redirects here. */
    explore: "/w/explore",
    /** Marvin Explore's booth mode: the unattended full-screen tour (admins). */
    exploreBooth: "/w/explore/booth",
} as const;

/** Every route pattern the `Document` route mounts at, most specific first: the web
 *  `App` and the desktop document tab both map these onto `<Route>`s, so prev/next
 *  sheet, version switches and callout destinations resolve the same way in both
 *  hosts. The `PATHS.*` builders above produce paths that match these. */
export const DOCUMENT_ROUTE_PATHS = [
    "/doc/:id/sg/:sectionGroupId/c/:connectionId",
    "/doc/:id/sg/:sectionGroupId/node/:fragmentNodeId",
    "/doc/:id/sg/:sectionGroupId",
    "/doc/:id/node/:fragmentNodeId",
    "/doc/:id?",
] as const;

// ID extraction
const ID_PROPERTIES = ["id", "file_id", "fragment_node_id"] as const;

const idFrom = (item: IdSource): string => {
    if (typeof item === "string") return item;
    if (!item || typeof item !== "object") throw new Error("Missing id");

    for (const prop of ID_PROPERTIES) {
        if (prop in item) {
            const value = (item as Record<string, unknown>)[prop];
            if (typeof value === "string") return value;
        }
    }

    throw new Error("Missing id");
};

/** The workbench path before the rename: an old link or bookmark still opens it, and App redirects it to Explore. */
export const LEGACY_WORKBENCH_PATH = "/w/workbench";

// Type guards for better type narrowing
const hasDocAndSectionGroup = (obj: unknown): obj is { doc: IdSource; sectionGroup: IdSource } => {
    return typeof obj === "object" && obj !== null && "doc" in obj && "sectionGroup" in obj;
};

const hasDocAndNode = (obj: unknown): obj is { doc: IdSource; node: IdSource } => {
    return typeof obj === "object" && obj !== null && "doc" in obj && "node" in obj;
};

const hasStringIds = (obj: unknown, ...fields: string[]): boolean => {
    if (typeof obj !== "object" || obj === null) return false;
    return fields.every((field) => field in obj && typeof (obj as any)[field] === "string");
};

// Provider-file viewer: full-page viewer for a synced-but-not-ingested
// provider file (PP-1085). Pattern and builder live together so the App.tsx
// route and every link that targets it cannot drift apart.
export const PROVIDER_FILE_VIEWER_ROUTE = "/documents/:category/file/:revisionId";
export const providerFileViewerPath = (categoryId: string, revisionId: string) =>
    `/documents/${categoryId}/file/${encodeURIComponent(revisionId)}`;

// Main route builders
export const documentRoutes = {
    document: (params: IdSource | { doc: IdSource; sectionGroup?: IdSource }) => {
        // Handle compound document/section group form
        if (typeof params === "object" && params !== null && "doc" in params) {
            const typed = params as { doc: IdSource; sectionGroup?: IdSource };
            const docId = idFrom(typed.doc);
            return typed.sectionGroup
                ? PATHS.sectionGroup(docId, idFrom(typed.sectionGroup))
                : PATHS.doc(docId);
        }
        // Handle simple ID form
        return PATHS.doc(idFrom(params));
    },

    sectionGroup: (
        params:
            { docId: string; sectionGroupId: string } | { doc: IdSource; sectionGroup: IdSource },
    ) => {
        if (hasDocAndSectionGroup(params)) {
            return PATHS.sectionGroup(idFrom(params.doc), idFrom(params.sectionGroup));
        }

        if (hasStringIds(params, "docId", "sectionGroupId")) {
            const { docId, sectionGroupId } = params as { docId: string; sectionGroupId: string };
            return PATHS.sectionGroup(docId, sectionGroupId);
        }

        throw new Error("Missing document or section group ID");
    },

    fragmentNode: (
        params:
            { doc: IdSource; node: IdSource } | { docId: string; nodeId: string } | ApiFragmentNode,
    ) => {
        if (hasDocAndNode(params)) {
            return PATHS.node(idFrom(params.doc), idFrom(params.node));
        }

        if (hasStringIds(params, "fileId", "id")) {
            const { fileId, id } = params as { fileId: string; id: string };
            return PATHS.node(fileId, id);
        }

        if (hasStringIds(params, "docId", "nodeId")) {
            const { docId, nodeId } = params as { docId: string; nodeId: string };
            return PATHS.node(docId, nodeId);
        }

        throw new Error("Missing document or node ID");
    },

    sectionGroupNode: (
        params:
            | { doc: IdSource; sectionGroup: IdSource; node: IdSource }
            | { docId: string; sectionGroupId: string; nodeId: string },
    ) => {
        if (
            typeof params === "object" &&
            params !== null &&
            "doc" in params &&
            "sectionGroup" in params &&
            "node" in params
        ) {
            const typed = params as { doc: IdSource; sectionGroup: IdSource; node: IdSource };
            return PATHS.sectionGroupNode(
                idFrom(typed.doc),
                idFrom(typed.sectionGroup),
                idFrom(typed.node),
            );
        }

        if (hasStringIds(params, "docId", "sectionGroupId", "nodeId")) {
            const { docId, sectionGroupId, nodeId } = params as {
                docId: string;
                sectionGroupId: string;
                nodeId: string;
            };
            return PATHS.sectionGroupNode(docId, sectionGroupId, nodeId);
        }

        throw new Error("Missing required IDs");
    },

    sectionGroupConnection: (
        params:
            | { doc: IdSource; sectionGroup: IdSource; connectionId: string }
            | { docId: string; sectionGroupId: string; connectionId: string },
    ) => {
        if (hasDocAndSectionGroup(params) && "connectionId" in params) {
            const typed = params as { doc: IdSource; sectionGroup: IdSource; connectionId: string };
            return PATHS.connection(
                idFrom(typed.doc),
                idFrom(typed.sectionGroup),
                typed.connectionId,
            );
        }

        if (hasStringIds(params, "docId", "sectionGroupId", "connectionId")) {
            const { docId, sectionGroupId, connectionId } = params as {
                docId: string;
                sectionGroupId: string;
                connectionId: string;
            };
            return PATHS.connection(docId, sectionGroupId, connectionId);
        }

        throw new Error("Missing required IDs");
    },

    /**
     * Smart alias route - returns UUID route if document is cached, otherwise alias route.
     * Use this for navigation links that should skip the redirect when possible.
     *
     * @param alias - Document alias (sheets, specs, ifoa)
     */
    alias: (alias: DocumentAlias): string => {
        if (cachedAliasedDocuments) {
            const docType = DOCUMENT_ALIASES[alias];
            const doc = cachedAliasedDocuments.find((d) => d.document_type === docType);
            if (doc) {
                return PATHS.doc(doc.id);
            }
        }
        return `/doc/${alias}`;
    },
};
/**
 * Build a SectionGroup URL with the native viewer's yellow search-highlight deep link.
 * Accepts bounds, a list of bounds (multi-occurrence citations, PP-1045 — encoded as
 * `;`-joined segments), or the backend's already-formatted value; segments use the
 * `page,xMin,yMin,xMax,yMax` contract parsed by `parseSearchHighlightsParam`.
 */
export function sectionGroupHighlightUrl(
    params: { docId: string; sectionGroupId: string },
    highlight: ApiBounds | ApiBounds[] | string,
): string {
    const value =
        typeof highlight === "string"
            ? highlight
            : Array.isArray(highlight)
              ? highlight.map(encodeBoundsParam).join(";")
              : encodeBoundsParam(highlight);
    return `${documentRoutes.sectionGroup(params)}?hl=${encodeURIComponent(value)}`;
}

/**
 * Build a SectionGroup URL with the overlay comparison deep link FileViewer parses.
 *
 * `layers` are the sheets drawn over the base, bottom first, each by its OWN PrimeFile id
 * (never a document id) and, when known, the section group that pins its revision. `zoomTo`
 * is an optional normalized `x0,y0,x1,y1` box on the base sheet, spelled by
 * `formatZoomToParam` and read back by `parseZoomToParam`. A page is never carried.
 */
export function sectionGroupOverlayUrl(
    params: { docId: string; sectionGroupId: string },
    layers: readonly OverlayLayerRef[],
    zoomTo?: string | null,
): string {
    return withOverlayDeepLink(documentRoutes.sectionGroup(params), layers, zoomTo);
}

/**
 * The one place that knows how a seeded Marvin Explore deep link is spelled (the
 * classic columns view). Callers pass a seed id (and a seed kind when the seed is a
 * tag connection rather than a fragment); the route reads both back out of the
 * query string.
 *
 * `seed_kind` is omitted for sections because the page already treats a missing
 * or unrecognized value as a section, so the shorter link is the canonical one.
 */
export type { WorkbenchSeedKind };

export function exploreUrl(seedId: string, seedKind?: WorkbenchSeedKind): string {
    const url = `${PATHS.explore}?seed=${encodeURIComponent(seedId)}`;
    return seedKind === "connection" ? `${url}&seed_kind=connection` : url;
}

/** @deprecated Alias of `exploreUrl`, kept for callers not yet renamed. */
export const workbenchUrl = exploreUrl;

/**
 * Marvin Explore opened on one sheet: `sheet` is the sheet's section group id (the
 * graph's sheet id) and `sel`, when given, the drawing selected on it.
 */
export function exploreSheetUrl(sheetId: string, sel?: string | null): string {
    const url = `${PATHS.explore}?sheet=${encodeURIComponent(sheetId)}`;
    return sel ? `${url}&sel=${encodeURIComponent(sel)}` : url;
}

/**
 * The constructability review, scrolled to one issue.
 *
 * `issue-` is `useVirtuosoHashScroll`'s prefix, and the review workflow is where that
 * hook is mounted (the `/w/cr` dashboard does not read the hash), so the fragment only
 * lands on `/w/cr/review`.
 */
export function constructabilityIssueRoute(issueId: string): string {
    return `${PATHS.constructabilityReview}#issue-${issueId}`;
}

const buildQueryString = (filters?: Record<string, string | number | undefined | null>) => {
    if (!filters) {
        return "";
    }

    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
        if (value === undefined || value === null) {
            return;
        }
        const stringValue = String(value);
        if (stringValue.length > 0) {
            params.append(key, stringValue);
        }
    });

    const query = params.toString();
    return query ? `?${query}` : "";
};

export const workflowRoutes = {
    constructabilityReview: (
        filters?: Record<string, string | number | undefined | null>,
    ): string => `${PATHS.constructabilityReview}${buildQueryString(filters)}`,
};

// Utilities
/** A sheet_overlay route's layers: `layers`, else the legacy single overlay (`overlaySheet`) as
 *  one layer, as the viewer reads that link (`parseOverlayLayers`). */
function navigationOverlayLayers(route: NavigationSuggestionRoute): OverlayLayerRef[] {
    if (route.layers?.length) return route.layers;
    return route.overlaySheet
        ? [{ fileId: route.overlaySheet, sectionGroupId: route.overlaySectionGroupId ?? null }]
        : [];
}

/**
 * The app path of a Marvin navigation card, built from its route's ids: what the server spells as
 * `route.href` (ai_engine `navigation_href`; agent/testdata/navigation_href_parity.json is this
 * function's output per kind). A card opens `route.href`; this stands in only for a card that
 * reached the client without one (stored before the server spelled it). The route ingredients
 * are resolved server-side (stable UUIDs and the sheet's OWNING document id, never the sheet's
 * PrimeFile id); this only assembles the path with the canonical builders. Null when a required
 * id is missing.
 */
export const marvinNavigationUrl = (
    kind: NavigationSuggestionKind,
    route: NavigationSuggestionRoute,
    options?: { exploreClassic?: boolean },
): string | null => {
    switch (kind) {
        case "sheet":
            return route.documentId && route.sectionGroupId
                ? documentRoutes.sectionGroup({
                      docId: route.documentId,
                      sectionGroupId: route.sectionGroupId,
                  })
                : null;
        case "sheet_region":
            // Same sheet route as `sheet`, plus a ?hl= deep link the viewer zooms to and
            // highlights (yellow rect). The backend pre-formats `hl` (page,xMin,yMin,xMax,yMax);
            // Document.tsx reads it via parseSearchHighlightParam on the sg route.
            return route.documentId && route.sectionGroupId && route.hl
                ? sectionGroupHighlightUrl(
                      { docId: route.documentId, sectionGroupId: route.sectionGroupId },
                      route.hl,
                  )
                : null;
        case "sheet_overlay":
            // Same sheet route as `sheet`, plus the ?overlayLayer= deep links FileViewer parses
            // into the overlay comparison viewer (each layer's own PrimeFile id and exact
            // revision, resolved server-side), and an optional ?zoomTo= spot on the base
            // sheet (parseZoomToParam). Mirrors OpeningOverlayEvidence's evidence link.
            if (!route.documentId || !route.sectionGroupId) return null;
            if (navigationOverlayLayers(route).length === 0) return null;
            return withOverlayDeepLink(
                documentRoutes.sectionGroup({
                    docId: route.documentId,
                    sectionGroupId: route.sectionGroupId,
                }),
                navigationOverlayLayers(route),
                route.zoomTo,
            );
        case "section":
            // A detail on a sheet routes under its section group; a section in a non-sheet
            // document is a document-level node. Both need documentId + nodeId.
            if (!route.documentId || !route.nodeId) return null;
            return route.sectionGroupId
                ? documentRoutes.sectionGroupNode({
                      docId: route.documentId,
                      sectionGroupId: route.sectionGroupId,
                      nodeId: route.nodeId,
                  })
                : documentRoutes.fragmentNode({ docId: route.documentId, nodeId: route.nodeId });
        case "workbench":
            // Marvin Explore on the fragment's sheet with the fragment selected. Classic, or
            // an old stored card without its sheet, seeds Explore on the fragment alone; the
            // server spells the same href (`marvinNavigationUrl.test.ts` holds the two to parity).
            if (!route.nodeId) return null;
            return route.sectionGroupId && !options?.exploreClassic
                ? exploreSheetUrl(route.sectionGroupId, route.nodeId)
                : exploreUrl(route.nodeId);
        case "document":
            return route.documentId ? documentRoutes.document(route.documentId) : null;
        case "rfi":
            return route.rfiId ? PATHS.rfi(route.rfiId) : null;
        case "submittal":
            return route.submittalId ? PATHS.submittal(route.submittalId) : null;
        case "submittal_register":
            return route.documentId ? PATHS.submittalRegister(route.documentId) : null;
        case "constructability_review":
            return PATHS.constructabilityDashboard;
        case "documents":
            return PATHS.documents;
        // Bare app pages — no entity id, fixed landing paths.
        case "home":
            return PATHS.home;
        case "search":
            return PATHS.search;
        case "control_tower":
            return PATHS.controlTower;
        case "rfis":
            return PATHS.rfis;
        case "submittals":
            return PATHS.submittals;
        case "rfi_assistant":
            return PATHS.rfiAssistant;
        case "submittal_registry":
            return PATHS.submittalRegistry;
        case "revisions":
            return PATHS.revisions;
        case "integrations":
            return PATHS.integrations;
        case "upload":
            return PATHS.upload;
        default:
            return null;
    }
};

export const openInNewWindow = (url: string) => window.open(scopedBrowserPath(url), "_blank");

/**
 * Append `revision` and (optional) `revision_mode` query params to a URL,
 * preserving any existing query string. No-op when `revision` is empty.
 *
 * Centralises the logic shared by viewer-side helpers (Document, sidebar list,
 * useNavigateWithSheets) and table-side deep links (DrawingSheetsTable) so
 * the param set and separator-handling don't drift across call sites.
 */
export const appendRevisionParams = (
    base: string,
    revision: string | null | undefined,
    revisionMode: string | null | undefined,
): string => {
    if (!revision) return base;
    const params = new URLSearchParams({ revision });
    if (revisionMode) params.set("revision_mode", revisionMode);
    const sep = base.includes("?") ? "&" : "?";
    return `${base}${sep}${params.toString()}`;
};

// Helper to find a section group by real ``(file_id, page)``.
export const findSectionGroup = (
    fileId: string,
    page: number | undefined,
    sectionGroups?: ApiSectionGroup[] | null,
): string | null => {
    return sectionGroupForFilePage(fileId, page, sectionGroups)?.id ?? null;
};

export const resolveFragmentNodeUrl = (
    node: ApiFragmentNode,
    sectionGroups?: ApiSectionGroup[] | null,
): string => {
    if (node.fileId) {
        const sgId = findSectionGroup(node.fileId, node.bounds?.page, sectionGroups);
        if (sgId) {
            // Sheet PFs are not document IDs — find the owning document via the SG.
            const sg = sectionGroups?.find((g) => g.id === sgId);
            return documentRoutes.sectionGroupNode({
                docId: sg?.document_id ?? node.fileId,
                sectionGroupId: sgId,
                nodeId: node.id,
            });
        }
    }

    const firstSg = sectionGroups?.[0];
    if (node.fileId && firstSg) {
        return documentRoutes.sectionGroupNode({
            docId: firstSg.document_id,
            sectionGroupId: firstSg.id,
            nodeId: node.id,
        });
    }

    // Last resort: document-level node
    return documentRoutes.fragmentNode(node);
};

/**
 * Parameters for building navigation URLs
 */
export interface NavigateParams {
    doc: string;
    sectionGroup?: string;
    fragmentNode?: string;
    connection?: string;
    /** Bounds to highlight as a yellow search rectangle (encoded as ?hl= for new-tab nav). */
    searchHighlight?: { page: number; xMin: number; yMin: number; xMax: number; yMax: number };
    /** Bounds to highlight as a blue drawing-reference circle (encoded as ?ref= for new-tab nav). */
    referenceHighlight?: { page: number; xMin: number; yMin: number; xMax: number; yMax: number };
    /** A plain-PDF page, retained without inventing a highlight rectangle. */
    page?: number;
}

/** Encode bounds for a highlight query param: "page,xMin,yMin,xMax,yMax". */
function encodeBoundsParam(b: {
    page: number;
    xMin: number;
    yMin: number;
    xMax: number;
    yMax: number;
}): string {
    return `${b.page},${b.xMin},${b.yMin},${b.xMax},${b.yMax}`;
}

/** Builds a navigation URL, always choosing the most specific route when IDs overlap. */
export function buildNavigationUrl(params: NavigateParams): string {
    const {
        doc,
        sectionGroup,
        fragmentNode,
        connection,
        searchHighlight,
        referenceHighlight,
        page,
    } = params;

    let path: string;

    // Most specific: section group + connection
    if (sectionGroup && connection) {
        path = `/doc/${doc}/sg/${sectionGroup}/c/${connection}`;
    }
    // Section group + fragment node
    else if (sectionGroup && fragmentNode) {
        path = `/doc/${doc}/sg/${sectionGroup}/node/${fragmentNode}`;
    }
    // Just section group
    else if (sectionGroup) {
        path = `/doc/${doc}/sg/${sectionGroup}`;
    }
    // Just fragment node (document-level)
    else if (fragmentNode) {
        path = `/doc/${doc}/node/${fragmentNode}`;
    }
    // Just document
    else {
        path = `/doc/${doc}`;
    }

    // Encode highlights as query params so they survive a new-tab open (router state
    // does not). ?hl= renders a yellow search rect; ?ref= a blue drawing-reference circle.
    const query: string[] = [];
    if (searchHighlight) query.push(`hl=${encodeURIComponent(encodeBoundsParam(searchHighlight))}`);
    if (referenceHighlight)
        query.push(`ref=${encodeURIComponent(encodeBoundsParam(referenceHighlight))}`);
    if (page !== undefined) query.push(`page=${encodeURIComponent(String(page))}`);

    return query.length > 0 ? `${path}?${query.join("&")}` : path;
}

/** Parse "page,xMin,yMin,xMax,yMax" highlight bounds from a query-param value. */
function parseBoundsParam(value: string | null): ApiBounds | null {
    if (!value) return null;
    const parts = value.split(",").map(Number);
    if (parts.length !== 5 || parts.some(isNaN)) return null;
    const [page, xMin, yMin, xMax, yMax] = parts;
    return { page, xMin, yMin, xMax, yMax };
}

/** Parse a `;`-separated list of highlight bounds (multi-occurrence ?hl=, PP-1045).
 *  Malformed segments are dropped; null when nothing valid remains. */
function parseBoundsListParam(value: string | null): ApiBounds[] | null {
    if (!value) return null;
    const bounds = value
        .split(";")
        .map(parseBoundsParam)
        .filter((b): b is ApiBounds => b !== null);
    return bounds.length > 0 ? bounds : null;
}

/** Yellow search-highlight bounds list from URL params (one rect per occurrence).
 *  Format: ?hl=page,xMin,yMin,xMax,yMax[;page,xMin,yMin,xMax,yMax…] */
export function parseSearchHighlightsParam(searchParams: URLSearchParams): ApiBounds[] | null {
    return parseBoundsListParam(searchParams.get("hl"));
}

/** First yellow search-highlight bounds from URL params (single-highlight consumers). */
export function parseSearchHighlightParam(searchParams: URLSearchParams): ApiBounds | null {
    return parseSearchHighlightsParam(searchParams)?.[0] ?? null;
}

/** A valid zero-based plain-PDF page. Malformed and negative values are ignored. */
export function parseDocumentPageParam(searchParams: URLSearchParams): number | null {
    const value = searchParams.get("page");
    if (!value || !/^\d+$/.test(value)) return null;
    const page = Number(value);
    return Number.isSafeInteger(page) ? page : null;
}

/** Blue drawing-reference highlight bounds from URL params. Format: ?ref=page,xMin,yMin,xMax,yMax */
export function parseReferenceHighlightParam(searchParams: URLSearchParams): ApiBounds | null {
    return parseBoundsParam(searchParams.get("ref"));
}

/**
 * Login redirect URL that returns the member to `target` after authenticating.
 * Single source of the /authenticate?redirect= contract.
 */
export function authenticateRedirectUrl(target: string): string {
    return `/authenticate?redirect=${encodeURIComponent(target)}`;
}
